import { FirebaseService, ToastService } from '@shared/lib/services';
import { formatInrDisplayAmount } from '@shared/lib/utils/inrAmountInWords';
import { Input } from '@shared/ui';
import { Button } from '@shared/ui/Button';
import { Form, Skeleton, Upload } from 'antd';
import { FileText, UploadCloud, X } from 'lucide-react';
import React, { useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useCreateAlterationRequest } from '../lib/hooks/useCreateAlterationRequest';
import useGetAlterationPrerequisites from '../lib/hooks/useGetAlterationPrerequisites';
import { ICampaignAlterationApprovalRequest, ICampaignAlterationRequestPayload } from '../lib/types';
import styles from './styles.module.scss';

export interface ProofAttachment {
  id: string;
  name: string;
  url?: string;
  file?: File;
}


export const filesToProofAttachments = (files: File[]): ProofAttachment[] => {
  return files.map((file) => ({
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: file.name,
    file,
  }));
};

interface ReimbursementFormProps {
  creatorName: string;
  briefInfluencerId: string;
  campaignInfluencerId?: string;
  briefId?: string;
  onSuccess?: () => void;
  onCancel: () => void;
  viewOnly?: boolean;
  alterationRequest?: ICampaignAlterationApprovalRequest;
}

export const ReimbursementForm: React.FC<ReimbursementFormProps> = ({
  creatorName,
  briefInfluencerId,
  campaignInfluencerId,
  briefId,
  onSuccess,
  onCancel,
  viewOnly = false,
  alterationRequest,
}) => {
  const [reimbursementAmount, setReimbursementAmount] = useState(
    alterationRequest ? String(alterationRequest.brandCostBaseCost || alterationRequest.influencerCostBaseCost || '') : ''
  );
  const [proofAttachments, setProofAttachments] = useState<ProofAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [proofUploadUrl, setProofUploadUrl] = useState<string>("");

  const { createAlteration, isCreating } = useCreateAlterationRequest(briefId, onSuccess);

  const amountNumber = Number(reimbursementAmount) || 0;

  const payload: ICampaignAlterationRequestPayload = {
    campaignInfluencerId: campaignInfluencerId || briefInfluencerId,
    type: "Reimbursement",
    toAlterBrandCost: true,
    brandCostBaseCost: amountNumber,
    brandCostDirection: "add",
    applyBrandCostServiceCostRate: false,
    applyBrandCostGstRate: false,
    toAlterInfluencerCost: true,
    influencerCostBaseCost: amountNumber,
    influencerCostDirection: "add",
    applyInfluencerCostGstRate: false,
    applyInfluencerCostTdsRate: false,
    remarks: `Reimbursement request for ${creatorName}`,
    proofUploadUrl: proofUploadUrl || undefined,
  };

  const [debouncedPayload] = useDebounce(() => payload, 250);

  const {
    alterationPrerequisite,
    isAlterationPrerequisiteFetching
  } = useGetAlterationPrerequisites(
    debouncedPayload(),
    !alterationRequest || alterationRequest?.status === "Pending"
  );

  const baseInfluencerCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.totalInfluencerBaseCost || 0
    : alterationRequest?.preCheckResult?.old?.totalInfluencerBaseCost || 0;

  const newInfluencerCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.totalInfluencerBaseCost || 0
    : alterationRequest?.preCheckResult?.new?.totalInfluencerBaseCost || 0;

  const baseBrandCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.totalBrandSellingCost || 0
    : alterationRequest?.preCheckResult?.old?.totalBrandSellingCost || 0;

  const newBrandCommercial = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.totalBrandSellingCost || 0
    : alterationRequest?.preCheckResult?.new?.totalBrandSellingCost || 0;

  const canSubmit = amountNumber > 0 && proofAttachments.length > 0 && proofUploadUrl !== "";

  const handleBeforeUpload = async (file: File) => {
    setProofAttachments((prev) => [...filesToProofAttachments([file])]);
    try {
      setIsUploading(true);
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        'alteration-proofs',
        file
      );
      setProofUploadUrl(uploadedUrl);
    } catch (error) {
      ToastService.error('Failed to upload proof file. Please try again.');
    } finally {
      setIsUploading(false);
    }
    return false; // Prevent antd default upload
  };

  const removeProofFile = (id: string) => {
    setProofAttachments((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      if (updated.length === 0) {
        setProofUploadUrl('');
      }
      return updated;
    });
  };

  const handleConfirm = () => {
    if (viewOnly) return;
    if (!canSubmit) return;

    createAlteration(payload);
  };

  return (
    <Form layout="vertical" onFinish={handleConfirm} className={styles.formContainer}>
      <Form.Item
        label={<span className={styles.label}>Reimbursement amount</span>}
        required
        style={{ marginBottom: 0 }}
      >
        <div className={styles.inputWrapper}>
          <Input
            type="number"
            min={0}
            prefix="₹"
            value={reimbursementAmount}
            onChange={(e) => setReimbursementAmount(e.target.value)}
            placeholder="Enter amount"
            autoFocus={!viewOnly}
            disabled={viewOnly}
          />
        </div>
      </Form.Item>

      <div className={styles.previewCard}>
        <p className={styles.previewTitle}>Ledger Preview</p>
        <div className={styles.previewList}>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>Influencer commercial</span>
            {isAlterationPrerequisiteFetching ? (
              <Skeleton.Input style={{ height: 20 }} size="small" />
            ) : (
              <span className={styles.previewValue}>
                {`${baseInfluencerCommercial != null ? formatInrDisplayAmount(baseInfluencerCommercial) : '—'} → ${newInfluencerCommercial != null ? formatInrDisplayAmount(newInfluencerCommercial) : '—'}`}
              </span>
            )}
          </div>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>Brand commercial</span>
            {isAlterationPrerequisiteFetching ? (
              <Skeleton.Input style={{ height: 20 }} size="small" />
            ) : (
              <span className={styles.previewValue}>
                {`${baseBrandCommercial != null ? formatInrDisplayAmount(baseBrandCommercial) : '—'} → ${newBrandCommercial != null ? formatInrDisplayAmount(newBrandCommercial) : '—'}`}
              </span>
            )}
          </div>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>Influencer ledger</span>
            <div className={styles.previewValueContainer}>
              <span className={styles.previewValue}>+{formatInrDisplayAmount(amountNumber)}</span>
              <p className={styles.previewHint}>No GST or TDS</p>
            </div>
          </div>
          <div className={styles.previewRow}>
            <span className={styles.previewLabel}>Brand ledger</span>
            <div className={styles.previewValueContainer}>
              <span className={styles.previewValue}>+{formatInrDisplayAmount(amountNumber)}</span>
              <p className={styles.previewHint}>No GST or TDS</p>
            </div>
          </div>
        </div>
      </div>

      {!viewOnly && (
        <Form.Item
          label={<span className={styles.label}>Proof of Purchase</span>}
          required
          extra={<p className={styles.modalDescription} style={{ margin: 0, marginTop: 4 }}>Attach receipts or payment proof for this reimbursement.</p>}
          style={{ marginBottom: 0 }}
        >
          <Upload.Dragger
            name="file"
            multiple={false}
            beforeUpload={handleBeforeUpload}
            fileList={proofAttachments.length ? [{uid: proofAttachments[0].id, name: proofAttachments[0].name, url: proofUploadUrl}] : []}
            onRemove={(file) => {
              removeProofFile(file.uid);
            }}
            showUploadList={true}
            accept=".pdf,.png,.jpg,.jpeg"
            maxCount={1}
            disabled={!!viewOnly}
          >
            <div className={styles.uploadPrompt}>
              <UploadCloud size={24} className={styles.icon} />
              <p className={styles.promptTitle}>
                <span>Click to upload</span> or drag and drop
              </p>
              <p className={styles.promptSub}>PDF, PNG, JPG up to 10MB</p>
            </div>
          </Upload.Dragger>
        </Form.Item>
      )}

      {viewOnly && (
        <Button
          type="link"
          size="small"
          onClick={() => {
            window.open(alterationRequest?.proofUploadUrl, "_blank");
          }}
          className={styles.advancedTaxToggle}
          style={{ width: "fit-content", marginTop: 8 }}
        >
          Proof of Alteration
        </Button>
      )}

      {!viewOnly && (
        <div className={styles.footer}>
          <button type="button" onClick={onCancel} className={styles.cancelBtn}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || isCreating || isUploading}
            className={styles.confirmBtn}
          >
            Apply Changes
          </button>
        </div>
      )}
    </Form>
  );
};
